import { useMemo, useRef } from 'react';
import { sbEditable } from '@storyblok/storyblok-editable';
import type { StoryblokComponent, StoryData } from 'storyblok-js-client';
import type { ArticleHeaderBlokData } from 'components/organisms/article-header/ArticleHeader.data';
import { useStoryblokEditorContext } from 'components/unlisted/StoryblokEditor';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';
import { getCampaignSectionTheme } from '@/data/campaign-theme';
import RelatedNewsCarousel from './RelatedNewsCarousel';

export type RelatedNewsBlok = StoryblokComponent<'related_news'> & {
  title?: string;
  use_accent_color?: boolean;
  section_background?: { color?: string };
  use_cream_background?: boolean;
  news_items?: (string | StoryData<ArticleHeaderBlokData>)[];
  previous_label?: string;
  next_label?: string;
};

export default function RelatedNews({ blok }: { blok: RelatedNewsBlok }) {
  const ref = useRef<HTMLDivElement>(null);
  const { isInEditorMode } = useStoryblokEditorContext();
  // Legacy cream is an alternate colour; the toggle still controls whether it is used.
  const background =
    blok.section_background?.color || (blok.use_cream_background ? '#f7f0e3' : undefined);
  const sectionTheme = useMemo(() => getCampaignSectionTheme(background), [background]);
  useThemeScrollBlock(ref, !!blok.use_accent_color, sectionTheme);
  const items = (blok.news_items || []).filter(
    (item): item is StoryData<ArticleHeaderBlokData> =>
      typeof item !== 'string' &&
      !!item?.content &&
      !!item.full_slug &&
      item.content.component === 'article' &&
      !item.content.hide_article,
  );
  return (
    <div ref={ref} {...sbEditable(blok)}>
      <RelatedNewsCarousel
        title={blok.title || 'Related\nnews'}
        items={items}
        previousLabel={blok.previous_label || 'Previous articles'}
        nextLabel={blok.next_label || 'Next articles'}
      />
      {!items.length && isInEditorMode && (
        <p
          style={{
            padding: '48px 24px',
            background: 'var(--background-color)',
            color: 'var(--foreground-color)',
          }}
        >
          Select articles in Related news to populate this carousel.
        </p>
      )}
    </div>
  );
}
